import { gql } from '@apollo/client';
import {
  DOCUMENT_SUMMARY_FIELDS,
  DOCUMENT_DETAIL_FIELDS,
  PAGE_INFO_FIELDS,
} from './fragments';

// ============================================================================
// Queries
// ============================================================================

/**
 * Paginated document list with filters.
 * Uses Relay cursor-based pagination — call fetchMore with `after` cursor.
 */
export const DOCUMENTS_QUERY = gql`
  query Documents(
    $filter: DocumentFilterInput
    $sortBy: SortField
    $sortDirection: SortDirection
    $first: Int = 20
    $after: String
    $allCustomers: Boolean
    $customerNumbers: [String!]
    $withPositions: Boolean = false
  ) {
    documents(
      filter: $filter
      sortBy: $sortBy
      sortDirection: $sortDirection
      first: $first
      after: $after
      allCustomers: $allCustomers
      customerNumbers: $customerNumbers
      withPositions: $withPositions
    ) {
      edges {
        node {
          ...DocumentSummaryFields
          searchContext @include(if: $withPositions) {
            lines {
              lineNumber
              articleNumber
              description
              description2
              quantity
              unit
              manufacturer
            }
            totalCount
          }
        }
        cursor
      }
      pageInfo {
        ...PageInfoFields
      }
      totalCount
      searchMatches {
        token
        kind
      }
    }
  }
  ${DOCUMENT_SUMMARY_FIELDS}
  ${PAGE_INFO_FIELDS}
`;

/**
 * Single document with full detail (including lines).
 */
export const DOCUMENT_QUERY = gql`
  query Document($id: ID!, $allCustomers: Boolean, $customerNumbers: [String!]) {
    document(id: $id, allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      ...DocumentDetailFields
    }
  }
  ${DOCUMENT_DETAIL_FIELDS}
`;

/**
 * Resolve a document by its number (doc_no, e.g. "VL0754213") to its UID.
 * Used by cross-reference UI (TEXT-line links, per-position jump buttons)
 * — faster and more reliable than the fulltext searchText path.
 *
 * Schmal gehalten (nur id + Identifikatoren) — der Modal-Open-Pfad kennt
 * danach die UID und kann den Rest via cache-first DOCUMENT_QUERY laden.
 */
export const DOCUMENT_BY_NUMBER_QUERY = gql`
  query DocumentByNumber($number: String!, $allCustomers: Boolean, $customerNumbers: [String!]) {
    documentByNumber(number: $number, allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      id
      documentNumber
      type
    }
  }
`;

/**
 * Document chain — predecessors and successors through NAV order chain.
 */
export const DOCUMENT_CHAIN_QUERY = gql`
  query DocumentChain($documentId: ID!, $allCustomers: Boolean, $customerNumbers: [String!]) {
    documentChain(documentId: $documentId, allCustomers: $allCustomers, customerNumbers: $customerNumbers) {
      id
      type
      documentNumber
      documentDate
      relation
      grossTotal
      lineCount
    }
  }
`;

/**
 * Presigned S3 URL for a single document PDF.
 * fetchPolicy should be 'network-only' — URLs expire after 1 hour.
 */
export const DOCUMENT_PDF_URL_QUERY = gql`
  query DocumentPdfUrl($documentId: ID!, $allCustomers: Boolean, $customerNumbers: [String!]) {
    documentPdfUrl(documentId: $documentId, allCustomers: $allCustomers, customerNumbers: $customerNumbers)
  }
`;

/**
 * Presigned S3 URL for bulk PDF download (ZIP).
 * fetchPolicy should be 'network-only' — URLs expire after 1 hour.
 */
export const DOCUMENT_BULK_PDF_URL_QUERY = gql`
  query DocumentBulkPdfUrl($documentIds: [ID!]!, $allCustomers: Boolean, $customerNumbers: [String!]) {
    documentBulkPdfUrl(documentIds: $documentIds, allCustomers: $allCustomers, customerNumbers: $customerNumbers)
  }
`;

/**
 * Fetch all matching documents (up to 1000) for overview CSV export.
 * Uses the standard documents query with a high limit and no cursor.
 */
export const DOCUMENTS_EXPORT_QUERY = gql`
  query DocumentsExport(
    $filter: DocumentFilterInput
    $sortBy: SortField
    $sortDirection: SortDirection
    $first: Int = 1000
    $allCustomers: Boolean
    $customerNumbers: [String!]
  ) {
    documents(
      filter: $filter
      sortBy: $sortBy
      sortDirection: $sortDirection
      first: $first
      allCustomers: $allCustomers
      customerNumbers: $customerNumbers
    ) {
      edges {
        node {
          ...DocumentSummaryFields
        }
      }
      totalCount
    }
  }
  ${DOCUMENT_SUMMARY_FIELDS}
`;

/**
 * Fetch documents WITH line items for position-detail CSV export.
 * Backend uses bulk loading (2 DB queries, no N+1).
 */
export const DOCUMENTS_FOR_EXPORT_QUERY = gql`
  query DocumentsForExport(
    $filter: DocumentFilterInput
    $sortBy: SortField
    $sortDirection: SortDirection
    $first: Int = 1000
    $allCustomers: Boolean
    $customerNumbers: [String!]
  ) {
    documentsForExport(
      filter: $filter
      sortBy: $sortBy
      sortDirection: $sortDirection
      first: $first
      allCustomers: $allCustomers
      customerNumbers: $customerNumbers
    ) {
      ...DocumentDetailFields
    }
  }
  ${DOCUMENT_DETAIL_FIELDS}
`;

// ============================================================================
// Mutations — Lesestatus (gelesen/ungelesen, Posteingang-Modell)
// ============================================================================

/**
 * Setzt den Gelesen-Status konkreter Belege. Genutzt für Auto-"gelesen" beim
 * Öffnen ([id], read: true) und die Bulk-Aktionen. Das zurückgegebene
 * `id`+`isRead` aktualisiert die normalisierten Document-Nodes im Apollo-Cache
 * automatisch (Listenzeilen schalten ohne Refetch um).
 */
export const SET_DOCUMENTS_READ_MUTATION = gql`
  mutation SetDocumentsRead(
    $documentIds: [ID!]!
    $read: Boolean!
    $allCustomers: Boolean
    $customerNumbers: [String!]
  ) {
    setDocumentsRead(
      documentIds: $documentIds
      read: $read
      allCustomers: $allCustomers
      customerNumbers: $customerNumbers
    ) {
      id
      isRead
    }
  }
`;

/**
 * Markiert alle sichtbaren Belege als gelesen ("Alles als gelesen"), optional
 * nach Belegtyp. Gibt die Anzahl betroffener Belege zurück. Aufrufer sollten
 * DASHBOARD_STATS_QUERY (+ ggf. DOCUMENTS_QUERY) refetchen.
 */
export const MARK_ALL_READ_MUTATION = gql`
  mutation MarkAllRead(
    $types: [DocumentType!]
    $allCustomers: Boolean
    $customerNumbers: [String!]
  ) {
    markAllRead(types: $types, allCustomers: $allCustomers, customerNumbers: $customerNumbers)
  }
`;

// ============================================================================
// Mutation — Angebot bestellen
// ============================================================================

/**
 * Bestellt ein Angebot verbindlich. Das zurückgegebene `id`+`isOrdered`+
 * `orderedAt` aktualisiert den normalisierten Document-Node im Apollo-Cache
 * automatisch (Bestell-Button schaltet ohne Refetch auf "bestellt").
 * Idempotent serverseitig: ein bereits bestelltes Angebot liefert unverändert
 * den bestellten Zustand zurück.
 */
export const ORDER_DOCUMENT_MUTATION = gql`
  mutation OrderDocument(
    $documentId: ID!
    $allCustomers: Boolean
    $customerNumbers: [String!]
  ) {
    orderDocument(
      documentId: $documentId
      allCustomers: $allCustomers
      customerNumbers: $customerNumbers
    ) {
      id
      isOrdered
      orderedAt
    }
  }
`;
